/**
 * Preview mode. Before launch (or with ?demo=1) the app runs against a simulated market that
 * behaves like the real one: a live price, the keeper's strike ladder priced with the server's
 * Black-Scholes, holder payouts, and a demo wallet whose trades update its own positions.
 * Nothing is signed or sent to any chain.
 *
 * Mode: VITE_PREVIEW=off forces live (set it at launch), =on forces preview. Otherwise ?demo=1 /
 * ?live=1 pick one for the session, and by default the app previews until the API reports a token.
 */
import { useSyncExternalStore } from "react";
import type { Address, Hash, Hex } from "viem";
import { API_URL, PREVIEW_MODE, SUPPLY } from "./config";
import { askPerUnit, bidPerUnit, delta, quoteVol } from "./bs";
import { toast } from "./toast";
import {
  ApiError,
  type Activity,
  type ApiConfig,
  type Candle,
  type ChainSeries,
  type Market,
  type OptionChain,
  type Positions,
  type RewardsResp,
  type SeriesRow,
  type SellQuote,
  type SignedQuote,
  type Stats,
  type UserOrder,
} from "./api";

// ------------------------------------------------------------------ mode

export type Mode = "probing" | "demo" | "live";
const PREVIEW = String(import.meta.env.VITE_PREVIEW ?? PREVIEW_MODE).toLowerCase();
const MODE_KEY = "strike:mode";
const WALLET_KEY = "strike:demo-wallet";

function store(key: string, value?: string): string | null {
  try {
    if (value !== undefined) sessionStorage.setItem(key, value);
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function forcedMode(): Mode | undefined {
  if (PREVIEW === "off") return "live";
  if (PREVIEW === "on") return "demo";
  const q = new URLSearchParams(window.location.search);
  if (q.has("demo")) store(MODE_KEY, "demo");
  else if (q.has("live")) store(MODE_KEY, "live");
  const v = store(MODE_KEY);
  return v === "demo" || v === "live" ? v : undefined;
}

let mode: Mode = forcedMode() ?? "probing";
const subs = new Set<() => void>();
const emit = () => subs.forEach((f) => f());
const subscribe = (f: () => void) => {
  subs.add(f);
  return () => void subs.delete(f);
};

let probing: Promise<Mode> | undefined;
export function resolveMode(): Promise<Mode> {
  if (mode !== "probing") return Promise.resolve(mode);
  probing ??= fetch(`${API_URL}/api/config`, { signal: AbortSignal.timeout(4000) })
    .then(async (r) => ((r.ok && (await r.json())?.contracts?.token) ? "live" : "demo") as Mode)
    .catch(() => "demo" as Mode)
    .then((m) => {
      mode = m;
      emit();
      return m;
    });
  return probing;
}

export const isDemo = () => mode === "demo";
export const useMode = () => useSyncExternalStore(subscribe, () => mode);

// ------------------------------------------------------------------ demo wallet

export const DEMO_ADDRESS = "0x7e57a11ce0de5f00dcafe0000000000000c0ffee" as Address;

const state = {
  connected: store(WALLET_KEY) === "1",
  seeded: false,
  longs: new Map<string, { series: SeriesRow; amount: bigint }>(),
  shorts: new Map<string, { series: SeriesRow; collateral: bigint; proceeds: bigint }>(),
  autoExercise: false,
  routerApproved: false,
  claimed: new Set<number>(),
  orders: [] as UserOrder[],
  filled: new Map<string, bigint>(),
  extraOi: new Map<string, bigint>(),
  mine: [] as Activity,
  strikeBalance: 25_000_000n * 10n ** 18n,
  strikeAllowance: 0n,
  wethBalance: 0n,
  wethAllowance: 0n,
  isOperator: false,
  stats: { premiums: 0, options: 0, fills: 0 },
};

// The demo book survives reloads within the tab.
const BOOK_KEY = "strike:demo-book";
const tag = (_k: string, v: unknown) =>
  typeof v === "bigint" ? { $b: v.toString() } : v instanceof Map ? { $m: [...v] } : v instanceof Set ? { $s: [...v] } : v;
const untag = (_k: string, v: unknown) => {
  if (v && typeof v === "object") {
    const o = v as { $b?: string; $m?: [unknown, unknown][]; $s?: unknown[] };
    if (o.$b !== undefined) return BigInt(o.$b);
    if (o.$m) return new Map(o.$m);
    if (o.$s) return new Set(o.$s);
  }
  return v;
};
function persist() {
  const { connected: _c, ...book } = state;
  store(BOOK_KEY, JSON.stringify(book, tag));
}
try {
  const saved = store(BOOK_KEY);
  if (saved) Object.assign(state, JSON.parse(saved, untag));
} catch {
  /* start fresh */
}

export const useDemoWallet = () => useSyncExternalStore(subscribe, () => state.connected);
export function connectDemo(on: boolean) {
  state.connected = on;
  store(WALLET_KEY, on ? "1" : "0");
  emit();
}

// ------------------------------------------------------------------ deterministic market

const E18 = 10n ** 18n;
const LAUNCH_TS = Date.UTC(2026, 8, 12, 18) / 1000; // the simulated coin graduated here
const P0 = 8.4e-7; // ETH per token: about a $2.2M market cap at $2,650
const now = () => Math.floor(Date.now() / 1000);
const toWei = (x: number) => BigInt(Math.round(x * 1e18)).toString();
const eth = (w: bigint | string) => Number(BigInt(w)) / 1e18;

function hash(n: number): number {
  let x = (n | 0) ^ 0x9e3779b9;
  x = Math.imul(x ^ (x >>> 16), 0x85ebca6b);
  x = Math.imul(x ^ (x >>> 13), 0xc2b2ae35);
  x ^= x >>> 16;
  return (x >>> 0) / 4294967296;
}
function hashStr(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) h = Math.imul(h ^ s.charCodeAt(i), 16777619);
  return hash(h);
}
function hex(seed: number, bytes: number): string {
  let out = "";
  for (let i = 0; out.length < bytes * 2; i++) out += Math.floor(hash(seed * 7919 + i) * 2 ** 32).toString(16).padStart(8, "0");
  return out.slice(0, bytes * 2);
}
const addr = (seed: number) => `0x${hex(seed, 20)}` as Address;

/** Smooth value noise in [-1, 1]. */
function vnoise(t: number, period: number, seed: number): number {
  const x = t / period;
  const k = Math.floor(x);
  const f = x - k;
  const s = f * f * (3 - 2 * f);
  const a = hash(k * 131 + seed) * 2 - 1;
  const b = hash((k + 1) * 131 + seed) * 2 - 1;
  return a + (b - a) * s;
}

const OCTAVES: [number, number][] = [
  [172_800, 0.2],
  [43_200, 0.085],
  [10_800, 0.04],
  [2_700, 0.018],
  [600, 0.008],
  [120, 0.0035],
  [20, 0.0014],
];

/** Price in ETH per token at any second: a climb since graduation plus layered noise. */
export function priceAt(t: number): number {
  let l = 0;
  OCTAVES.forEach(([p, a], i) => (l += a * vnoise(t, p, i * 7 + 1)));
  const age = Math.max(0, t - LAUNCH_TS) / 86_400;
  return P0 * Math.exp(l + 1.1 * (1 - Math.exp(-age / 4)) - 1.1);
}
const twapAt = (t: number) => {
  let s = 0;
  for (let i = 0; i < 10; i++) s += priceAt(t - i * 30);
  return s / 10;
};
const ethUsdAt = (t: number) => 2650 * Math.exp(0.012 * vnoise(t, 7200, 99));

function realizedVol(t: number): number {
  let sum = 0;
  for (let i = 0; i < 24; i++) {
    const r = Math.log(priceAt(t - i * 3600) / priceAt(t - (i + 1) * 3600));
    sum += r * r;
  }
  return Math.sqrt(sum / 24) * Math.sqrt(24 * 365);
}

function candles(tf: number, t: number): Candle[] {
  const end = Math.floor(t / tf) * tf;
  const start = Math.max(Math.ceil(LAUNCH_TS / tf) * tf, end - 299 * tf);
  const out: Candle[] = [];
  for (let c = start; c <= end; c += tf) {
    const stop = Math.min(c + tf, t);
    let h = -Infinity;
    let l = Infinity;
    for (let i = 0; i <= 12; i++) {
      const p = priceAt(c + ((stop - c) * i) / 12);
      h = Math.max(h, p);
      l = Math.min(l, p);
    }
    out.push({ t: c, o: priceAt(c), h, l, c: priceAt(stop), v: (0.4 + hash(c) * 1.6) * (tf / 300) * 0.4 });
  }
  return out;
}

// ------------------------------------------------------------------ contracts and series

const WETH = "0x0Bd7D308f8E1639FAb988df18A8011f41EAcAD73" as Address;
const C = {
  options: addr(1),
  exchange: addr(2),
  vault: addr(3),
  router: addr(4),
  treasury: addr(5),
  distributor: addr(6),
  timelock: addr(7),
  token: addr(8),
  weth: WETH,
};

/** Every series the demo has shown, so positions and orders survive a relist. */
const known = new Map<string, SeriesRow>();

// Same schedule and ladders as server/src/keeper/series.ts.
const CALLS = [1, 1.1, 1.25, 1.5, 1.75, 2, 2.5, 3, 4, 5];
const PUTS = [1, 0.9, 0.8, 0.7, 0.6, 0.5, 0.4];
const SHORT_CALLS = [1, 1.1, 1.25, 1.5, 2];
const SHORT_PUTS = [1, 0.9, 0.8, 0.7, 0.5];
const nice = (x: number) => Number(x.toPrecision(2));

type Expiry = { ts: number; kind: "daily" | "weekly" | "monthly" };

function schedule(t: number): Expiry[] {
  const maxT = 60 * 86_400;
  const ok = (e: number) => e - t >= 3 * 3600 && e - t <= maxT;
  const out = new Map<number, Expiry["kind"]>();
  const dayAt = (offset: number) => {
    const d = new Date(t * 1000);
    d.setUTCHours(20, 0, 0, 0);
    d.setUTCDate(d.getUTCDate() + offset);
    return Math.floor(d.getTime() / 1000);
  };
  for (let i = 0, n = 0; n < 3 && i < 14; i++) if (ok(dayAt(i))) (out.set(dayAt(i), "daily"), n++);
  for (let e = dayAt((5 - new Date(t * 1000).getUTCDay() + 7) % 7), n = 0; n < 4 && e - t <= maxT; e += 7 * 86_400)
    if (ok(e)) (out.set(e, "weekly"), n++);
  const d0 = new Date(t * 1000);
  for (let m = 0, n = 0; n < 2 && m < 6; m++) {
    const last = new Date(Date.UTC(d0.getUTCFullYear(), d0.getUTCMonth() + m + 1, 0, 20));
    last.setUTCDate(last.getUTCDate() - ((last.getUTCDay() - 5 + 7) % 7));
    const e = Math.floor(last.getTime() / 1000);
    if (ok(e)) (out.set(e, "monthly"), n++);
  }
  return [...out].map(([ts, kind]) => ({ ts, kind })).sort((a, b) => a.ts - b.ts);
}
const nextExpiries = (t: number) => schedule(t).map((e) => e.ts);
const LIST_LEAD = { daily: 3 * 86_400, weekly: 28 * 86_400, monthly: 60 * 86_400 };

function seriesRow(expiry: number, strike: number, isCall: boolean): SeriesRow {
  return {
    id: `${expiry}${String(Math.round(strike * 1e13)).padStart(9, "0")}${isCall ? 1 : 0}`,
    underlying: C.token,
    quote: WETH,
    strike: toWei(strike),
    expiry,
    is_call: isCall ? 1 : 0,
  };
}

/** What the keeper would have listed: the ladder when the expiry entered the schedule, plus relists as the price drifted. */
function listed(t: number): SeriesRow[] {
  const hour = Math.floor(t / 3600) * 3600;
  const rows: SeriesRow[] = [];
  for (const { ts: expiry, kind } of schedule(t)) {
    const listedAt = Math.max(LAUNCH_TS, Math.min(expiry - LIST_LEAD[kind], hour - 7200));
    const short = expiry - t < 3 * 86_400;
    const picked: { k: number; isCall: boolean }[] = [];
    for (const ref of [priceAt(listedAt), priceAt(hour - 3600)])
      for (const [ladder, isCall] of [
        [short ? SHORT_CALLS : CALLS, true],
        [short ? SHORT_PUTS : PUTS, false],
      ] as const)
        for (const m of ladder) {
          const raw = nice(ref * m);
          if (picked.some((x) => x.isCall === isCall && Math.abs(x.k / raw - 1) < 0.04)) continue;
          // Share a strike with the other side when one is close, so the chain reads as clean rows.
          const k = picked.find((x) => Math.abs(x.k / raw - 1) < 0.04)?.k ?? raw;
          if (picked.length < 32) picked.push({ k, isCall });
        }
    rows.push(...picked.map((p) => seriesRow(expiry, p.k, p.isCall)));
  }
  for (const r of rows) known.set(r.id, r);
  return rows;
}

function openInterest(s: SeriesRow): bigint {
  const base = s.is_call ? 1_500_000 + hashStr(s.id) * 36_000_000 : 600_000 + hashStr(s.id) * 12_000_000;
  return BigInt(Math.round(base / 50_000) * 50_000) * E18 + (state.extraOi.get(s.id) ?? 0n);
}

// The simulated vault: 100M $STRIKE bought at launch and 4 WETH earned since. Same caps as
// HouseVault at launch: a series uses at most 10% of that collateral, one buy at most 5%.
const VAULT_STRIKE = 100_000_000;
const VAULT_WETH = 4;
function capacity(s: SeriesRow, oi: bigint): bigint {
  const free = s.is_call ? VAULT_STRIKE : VAULT_WETH / eth(s.strike); // in options
  const vaultOi = Math.min(eth(oi) * 0.6, free * 0.1); // most open interest was sold by the vault
  const room = Math.max(0, Math.min(free * 0.1 - vaultOi, free * 0.05));
  return BigInt(Math.floor(room)) * E18;
}

// The preview vault buys back any size (the real one buys up to what it wrote on the series).
const VAULT_BID_CAPACITY = (10n ** 27n).toString();

function chain(t: number): OptionChain {
  const spot = twapAt(t);
  const sigma = quoteVol(realizedVol(t));
  const series: ChainSeries[] = listed(t).map((s) => {
    const k = eth(s.strike);
    const isCall = s.is_call === 1;
    const oi = openInterest(s);
    return {
      id: s.id,
      isCall,
      strikeWei: s.strike,
      strikeEth: k,
      expiry: s.expiry,
      askEth: askPerUnit(spot, k, s.expiry - t, sigma, isCall),
      delta: delta(spot, k, s.expiry - t, sigma, isCall),
      moneyness: k / spot,
      openInterest: oi.toString(),
      vaultCapacity: capacity(s, oi).toString(),
      bidEth: bidPerUnit(spot, k, s.expiry - t, sigma, isCall),
      vaultBidCapacity: VAULT_BID_CAPACITY,
    };
  });
  return { open: true, sigma, spotWei: toWei(spot), series };
}

const findSeries = (id: string) => known.get(id);

// ------------------------------------------------------------------ market, stats, payouts

function market(t: number): Market {
  const p = priceAt(t);
  const eu = ethUsdAt(t);
  return {
    launched: true,
    phase: 2,
    spotWei: toWei(p),
    twapWei: toWei(twapAt(t)),
    priceEth: p,
    priceUsd: p * eu,
    ethUsd: eu,
    marketCapUsd: p * SUPPLY * eu,
    volume24hEth: 24 + 22 * vnoise(t, 86_400, 17) + 10,
    change24hPct: (p / priceAt(t - 86_400) - 1) * 100,
    realizedVol: realizedVol(t),
  };
}

const EPOCH = 43_200;
const FIRST_EPOCH = Math.ceil(LAUNCH_TS / EPOCH);
/** ETH paid to all holders by epoch number (end timestamp = epoch * 12h). */
const epochPot = (e: number) => (0.45 + 0.85 * hash(e * 17 + 3)) * Math.min(1, (e - FIRST_EPOCH + 1) / 4);
const epochShare = 0.0094; // the demo wallet's share of the average supply held

function stats(t: number): Stats {
  const last = Math.floor(t / EPOCH);
  let paid = 0;
  let unclaimed = 0;
  for (let e = FIRST_EPOCH; e <= last; e++) {
    const pot = epochPot(e);
    const open = last - e < 2 ? 0.55 : 0.07;
    paid += pot * (1 - open);
    unclaimed += pot * open;
  }
  const hours = Math.max(0, t - LAUNCH_TS) / 3600;
  return {
    paidToHolders: toWei(paid),
    fundedToHolders: toWei(paid + unclaimed),
    allocatedUnclaimed: toWei(unclaimed),
    nextPayoutPot: toWei(epochPot(last + 1) * ((t % EPOCH) / EPOCH)),
    premiumsWeth: toWei(hours * 0.036 + state.stats.premiums),
    optionsTraded: toWei(hours * 7_400_000 + state.stats.options),
    fills: Math.floor(hours * 2.7) + state.stats.fills,
  };
}

function rewards(account: string, t: number): RewardsResp {
  if (account.toLowerCase() !== DEMO_ADDRESS) return { account, epochs: [] };
  const last = Math.floor(t / EPOCH);
  const epochs = [];
  for (let e = last; e > last - 8 && e >= FIRST_EPOCH; e--) {
    epochs.push({
      epoch: e - FIRST_EPOCH + 1,
      endTs: e * EPOCH,
      amount: toWei(epochPot(e) * epochShare * (0.85 + 0.3 * hash(e))),
      proof: [] as Hex[],
      claimed: last - e >= 3 || state.claimed.has(e - FIRST_EPOCH + 1),
    });
  }
  return { account, epochs };
}

// ------------------------------------------------------------------ activity tape

const SIZES = [250_000, 500_000, 1_000_000, 2_000_000, 2_500_000, 5_000_000, 10_000_000];
const SLOT = 70;

function activity(t: number, limit: number): Activity {
  const rows = listed(t);
  const ch = chain(t);
  const out: Activity = [...state.mine];
  for (let slot = Math.floor(t / SLOT); out.length < limit && slot > Math.floor(t / SLOT) - 400; slot--) {
    if (hash(slot * 3 + 1) > 0.72) continue;
    const ts = Math.min(t - 1, slot * SLOT + Math.floor(hash(slot * 3 + 2) * SLOT));
    const r = hash(slot * 3 + 3);
    const s = rows[Math.floor(hash(slot * 5 + 7) * rows.length)];
    const amt = SIZES[Math.floor(hash(slot * 5 + 9) * SIZES.length)];
    const who = addr(10_000 + (slot % 997));
    const base = { block: 4_000_000 + (ts - LAUNCH_TS) * 10, ts, tx: `0x${hex(slot, 32)}` as Hex };
    if (r < 0.64 && s) {
      const ask = ch.series.find((x) => x.id === s.id)?.askEth ?? 0;
      out.push({
        ...base,
        name: "Filled",
        args: { maker: C.vault, taker: who, seriesId: s.id, side: "0", amount: toWei(amt), premium: toWei(ask * amt), fee: toWei(ask * amt * 0.1) },
      });
    } else if (r < 0.82 && s) {
      out.push({ ...base, name: "CashedOut", args: { holder: who, seriesId: s.id, amount: toWei(amt), profit: toWei(0.02 + hash(slot) * 0.6) } });
    } else {
      out.push({ ...base, name: "Claimed", args: { account: who, amount: toWei(0.002 + hash(slot * 9) * 0.05) } });
    }
  }
  return out.slice(0, limit);
}

function logMine(name: string, args: Record<string, string>) {
  state.mine.unshift({ block: 0, ts: now(), tx: `0x${hex(now(), 32)}` as Hex, name, args: { ...args, taker: DEMO_ADDRESS, holder: DEMO_ADDRESS } });
  state.mine.splice(10);
}

// ------------------------------------------------------------------ the demo wallet's book

function seed() {
  if (state.seeded) return;
  state.seeded = true;
  const t = now();
  const spot = priceAt(t);
  const exps = nextExpiries(t);
  const e1 = exps[0];
  const e2 = exps.find((e) => e - t > 5 * 86_400) ?? exps[1];
  const ch = listed(t);
  const near = (expiry: number, isCall: boolean, m: number) =>
    ch
      .filter((s) => s.expiry === expiry && (s.is_call === 1) === isCall)
      .sort((a, b) => Math.abs(eth(a.strike) / spot - m) - Math.abs(eth(b.strike) / spot - m))[0];
  // An older call that is now in the money, so "Cash out" has something to do.
  const itm = seriesRow(e1, nice(spot * 0.72), true);
  known.set(itm.id, itm);
  const book: [SeriesRow | undefined, number][] = [
    [itm, 1_500_000],
    [near(e1, true, 1.25), 2_500_000],
    [near(e1, false, 0.8), 1_000_000],
    [near(e2, true, 2), 5_000_000],
  ];
  for (const [s, n] of book) {
    if (!s) continue;
    const cur = state.longs.get(s.id)?.amount ?? 0n;
    state.longs.set(s.id, { series: s, amount: cur + BigInt(n) * E18 });
  }
  const sold = near(e2, true, 1.5);
  if (sold) state.shorts.set(sold.id, { series: sold, collateral: 3_000_000n * E18, proceeds: 0n });

  // A few holders undercutting the vault on popular strikes.
  const c = chain(t);
  for (const [i, s] of [near(e1, true, 1.25), near(e1, true, 1.5), near(e2, true, 1.25), near(e1, false, 0.8)].entries()) {
    if (!s) continue;
    const ask = c.series.find((x) => x.id === s.id)?.askEth ?? 0;
    for (let j = 0; j < 2; j++) {
      state.orders.push({
        digest: `0x${hex(500 + i * 10 + j, 32)}` as Hex,
        maker: addr(900 + i * 10 + j),
        series_id: s.id,
        side: 0,
        amount: toWei((1 + Math.floor(hash(i * 7 + j) * 4)) * 1_000_000),
        price: toWei(ask * (0.93 + j * 0.03)),
        premium_token: WETH,
        min_spot: "0",
        max_spot: "0",
        deadline: s.expiry - 600,
        nonce: String(1000 + i * 10 + j),
        signature: `0x${hex(700 + i, 65)}` as Hex,
      });
    }
  }
  persist();
}

function positions(account: string): Positions {
  if (account.toLowerCase() !== DEMO_ADDRESS) return { longs: [], shorts: [], autoExercise: false, routerApproved: false };
  seed();
  return {
    longs: [...state.longs.values()].map((l) => ({ series: l.series, amount: l.amount.toString() })),
    shorts: [...state.shorts.values()].map((s) => ({ series: s.series, collateral: s.collateral.toString(), proceeds: s.proceeds.toString() })),
    autoExercise: state.autoExercise,
    routerApproved: state.routerApproved,
  };
}

function quote(body: { seriesId: string; amount: string; premiumToken?: string; side?: string }): SignedQuote | SellQuote {
  const t = now();
  const s = chain(t).series.find((x) => x.id === body.seriesId);
  if (!s) throw new ApiError(404, "unknown_series", "unknown series");
  const amount = BigInt(body.amount);
  if (body.side === "sell") return sellQuote(s, amount, t);
  if (amount > BigInt(s.vaultCapacity)) {
    throw new ApiError(400, "capacity", `vault can sell at most ${Math.floor(eth(s.vaultCapacity)).toLocaleString("en-US")} on this series right now`);
  }
  const spot = twapAt(t);
  const price = BigInt(toWei(s.askEth));
  return {
    order: {
      maker: C.vault,
      taker: "0x0000000000000000000000000000000000000000",
      seriesId: s.id,
      side: 0,
      amount: amount.toString(),
      price: price.toString(),
      premiumToken: WETH,
      minSpot: toWei(spot * 0.975),
      maxSpot: toWei(spot * 1.025),
      deadline: String(t + 60),
      nonce: String(t),
    },
    signature: `0x${hex(t, 65)}` as Hex,
    premium: ((amount * price + E18 - 1n) / E18).toString(),
    premiumToken: "WETH",
    askEthPerOption: s.askEth,
    sigma: chain(t).sigma ?? 1.5,
    spotWei: toWei(spot),
    expiresAt: t + 60,
  };
}

function sellQuote(s: ChainSeries, amount: bigint, t: number): SellQuote {
  const price = BigInt(toWei(s.bidEth ?? 0));
  const premium = (amount * price) / E18;
  if (premium === 0n) throw new ApiError(409, "no_bid", "the vault is not buying this option right now");
  const spot = twapAt(t);
  return {
    order: {
      maker: C.vault,
      taker: "0x0000000000000000000000000000000000000000",
      seriesId: s.id,
      side: 2,
      amount: amount.toString(),
      price: price.toString(),
      premiumToken: WETH,
      minSpot: toWei(spot * 0.975),
      maxSpot: toWei(spot * 1.025),
      deadline: String(t + 60),
      nonce: String(t),
    },
    signature: `0x${hex(t, 65)}` as Hex,
    side: "sell",
    premium: premium.toString(),
    proceeds: (premium - premium / 10n).toString(),
    premiumToken: "WETH",
    bidEthPerOption: s.bidEth ?? 0,
    sigma: chain(t).sigma ?? 1.5,
    spotWei: toWei(spot),
    expiresAt: t + 60,
  };
}

// ------------------------------------------------------------------ API

const config: ApiConfig = {
  chainId: 4663,
  explorer: "https://robinhoodchain.blockscout.com",
  contracts: C,
  launch: { token: C.token, curve: addr(9), phase: 2, graduationThreshold: "4200000000000000000", creatorTaxBps: 300 },
  params: { bandBps: 250, quoteTtlSec: 60, epochHoursUtc: [0, 12], minHoldingUsd: 10, expiryHourUtc: 20 },
};

export async function demoApi<T>(path: string, init?: RequestInit): Promise<T> {
  const url = new URL(path, "https://demo.local");
  const q = url.searchParams;
  const p = url.pathname;
  const t = now();
  const body = init?.body ? JSON.parse(String(init.body)) : undefined;
  let out: unknown;
  if (p === "/api/config") out = config;
  else if (p === "/api/market") out = market(t);
  else if (p === "/api/candles") out = candles(Number(q.get("tf") ?? 300), t);
  else if (p === "/api/chain") out = chain(t);
  else if (p === "/api/stats") out = stats(t);
  else if (p === "/api/activity") out = activity(t, Number(q.get("limit") ?? 20));
  else if (p.startsWith("/api/positions/")) out = positions(p.split("/")[3]);
  else if (p.startsWith("/api/rewards/")) out = rewards(p.split("/")[3], t);
  else if (p === "/api/quote") out = quote(body);
  else if (p === "/api/orders" && init?.method === "POST") {
    const o = body.order as SignedQuote["order"];
    const digest = `0x${hex(t + state.orders.length, 32)}` as Hex;
    state.orders.push({
      digest,
      maker: DEMO_ADDRESS,
      series_id: o.seriesId,
      side: o.side,
      amount: o.amount,
      price: o.price,
      premium_token: o.premiumToken,
      min_spot: o.minSpot,
      max_spot: o.maxSpot,
      deadline: Number(o.deadline),
      nonce: o.nonce,
      signature: body.signature,
    });
    persist();
    out = { digest };
  } else if (p === "/api/orders") {
    seed();
    const id = q.get("seriesId");
    out = state.orders.filter((o) => !id || o.series_id === id);
  } else throw new ApiError(404, "not_found", "not found");
  await new Promise((r) => setTimeout(r, 60 + Math.random() * 120));
  return out as T;
}

// ------------------------------------------------------------------ simulated transactions

type Call = { address: Address; functionName: string; args?: readonly unknown[]; value?: bigint };
const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

function intrinsicWei(s: SeriesRow, amount: bigint): bigint {
  const spot = priceAt(now());
  const k = eth(s.strike);
  const per = s.is_call ? Math.max(0, spot - k) : Math.max(0, k - spot);
  return BigInt(toWei(per * eth(amount) * 0.97)); // after the swap and pool fees
}

function apply({ address, functionName, args = [], value = 0n }: Call) {
  seed();
  const a = args as never[];
  switch (functionName) {
    case "buyWithEth": {
      const order = a[0] as { maker: Address; seriesId: bigint; nonce: bigint; price: bigint };
      const amount = a[2] as bigint;
      const id = order.seriesId.toString();
      const s = findSeries(id);
      if (!s) throw new Error("This series is no longer listed.");
      const cur = state.longs.get(id);
      state.longs.set(id, { series: s, amount: (cur?.amount ?? 0n) + amount });
      state.extraOi.set(id, (state.extraOi.get(id) ?? 0n) + amount);
      const premium = eth((amount * order.price) / E18);
      state.stats.premiums += premium;
      state.stats.options += eth(amount);
      state.stats.fills += 1;
      const ask = state.orders.find((o) => o.maker === order.maker && o.nonce === order.nonce.toString());
      if (ask) state.filled.set(ask.digest, (state.filled.get(ask.digest) ?? 0n) + amount);
      logMine("Filled", { maker: order.maker, seriesId: id, side: "0", amount: amount.toString(), premium: toWei(premium) });
      break;
    }
    case "cashOut": {
      const id = (a[0] as bigint).toString();
      const l = state.longs.get(id);
      if (!l) throw new Error("Nothing to cash out.");
      const amount = a[1] as bigint;
      const profit = intrinsicWei(l.series, amount);
      if (profit === 0n) throw new Error("This option is out of the money.");
      if (amount >= l.amount) state.longs.delete(id);
      else state.longs.set(id, { ...l, amount: l.amount - amount });
      logMine("CashedOut", { seriesId: id, amount: amount.toString(), profit: profit.toString() });
      break;
    }
    case "sellForEth": {
      const order = a[0] as { seriesId: bigint; price: bigint };
      const id = order.seriesId.toString();
      const l = state.longs.get(id);
      const amount = a[2] as bigint;
      if (!l || amount > l.amount) throw new Error("You don't hold that many options.");
      if (amount === l.amount) state.longs.delete(id);
      else state.longs.set(id, { ...l, amount: l.amount - amount });
      state.extraOi.set(id, (state.extraOi.get(id) ?? 0n) - amount);
      const premium = (amount * order.price) / E18;
      state.stats.premiums += eth(premium);
      state.stats.fills += 1;
      logMine("Filled", { maker: C.vault, seriesId: id, side: "2", amount: amount.toString(), premium: premium.toString() });
      break;
    }
    case "setAutoExercise":
      state.autoExercise = Boolean(a[0]);
      break;
    case "setApprovalForAll":
      state.routerApproved = true;
      break;
    case "claimMany":
      for (const e of a[0] as bigint[]) state.claimed.add(Number(e));
      break;
    case "claimProceeds": {
      const s = state.shorts.get((a[0] as bigint).toString());
      if (s) s.proceeds = 0n;
      break;
    }
    case "redeem":
      state.shorts.delete((a[0] as bigint).toString());
      break;
    case "cancel": {
      const o = a[0] as { nonce: bigint };
      state.orders = state.orders.filter((x) => !(x.maker === DEMO_ADDRESS && x.nonce === o.nonce.toString()));
      break;
    }
    case "approve":
      if (address.toLowerCase() === C.token.toLowerCase()) state.strikeAllowance = 2n ** 255n;
      else state.wethAllowance = 2n ** 255n;
      break;
    case "deposit":
      state.wethBalance += value;
      break;
    case "setWriteOperator":
      state.isOperator = true;
      break;
  }
}

/** Plays a transaction's lifecycle with toasts and applies its effect to the demo book. */
export async function demoTx(label: string, call: Call): Promise<Hash> {
  if (!state.connected) throw new Error("Connect your wallet first.");
  const id = toast.show("pending", `${label}: confirm in your wallet`);
  try {
    await sleep(700);
    toast.update(id, "pending", `${label}: confirming…`);
    await sleep(900);
    apply(call);
    persist();
    toast.update(id, "ok", `${label}: done. Preview only, nothing was sent.`);
    emit();
    return `0x${hex(now(), 32)}` as Hash;
  } catch (err) {
    toast.update(id, "err", err instanceof Error ? err.message : "Something went wrong.");
    throw err;
  }
}

export async function demoSimulate<T>(call: Call): Promise<T> {
  if (call.functionName === "sellForEth") {
    const [order, , amount] = (call.args ?? []) as [{ price: bigint }, unknown, bigint];
    const premium = (amount * order.price) / E18;
    return (premium - premium / 10n) as T;
  }
  if (call.functionName === "cashOut") {
    const l = state.longs.get(((call.args ?? [])[0] as bigint).toString());
    return (l ? intrinsicWei(l.series, (call.args ?? [])[1] as bigint) : 0n) as T;
  }
  return undefined as T;
}

export async function demoSign(): Promise<Hex> {
  const id = toast.show("pending", "Sign the order in your wallet");
  await sleep(800);
  toast.dismiss(id);
  return `0x${hex(now(), 65)}` as Hex;
}

export const demoFilled = (digests: readonly string[]) => digests.map((d) => state.filled.get(d) ?? 0n);

export function demoWriterState() {
  return {
    strikeBalance: state.strikeBalance,
    strikeAllowance: state.strikeAllowance,
    wethBalance: state.wethBalance,
    wethAllowance: state.wethAllowance,
    isOperator: state.isOperator,
  };
}
